import {
    Card,
    Grid,
    IconButton, Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, Typography
} from "@mui/material";
import React from "react";
import {formatDurationAbbr, SmartLink} from "../abelcommon";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {Link as RouterLink} from "react-router-dom";
import {useBlockTxs} from "../queries";
import QueryProcessor from "./QueryProcessor";

interface BlockTxItemProps {
    txid: string;
    inputs: number;
    outputs: number;
    fee: number;
    version: number;
    size: number;
    fullSize: number;
}


const BlockTxItem = ({txid, inputs, outputs, fee, version, size, fullSize}: BlockTxItemProps) => {
    return (
        <Card sx={{
            margin: 1,
            padding: 1,
            display: 'flex',
            boxShadow: 3,
            borderRadius: 2,
            width: 1,
            minHeight: 66,
            height: 66,
            overflow: 'auto',
        }}>
            <Grid container direction="row" spacing={1}>
                <Grid item xs={8}>
                    <Typography variant="body2">
                        {`TXID: `}
                        <Link component={RouterLink} to={`/tx/0x${txid}`}>
                            {txid}...
                        </Link>
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="body2">
                        {`Version: ${version}`}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="body2">
                        {`Value: ${fee} ABEL`}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="body2">
                        {`Size: ${size}`}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="body2">
                        {`Full Size: ${fullSize}`}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="body2">
                        {`From: ${inputs}`}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="body2">
                        {`To: ${outputs}`}
                    </Typography>
                </Grid>
            </Grid>
        </Card>
    );
}

function BlockRow({data, i, allowExpand}: { data: any, i: number, allowExpand: boolean }) {
    const dts = data.time * 1000;
    const ts = new Date();
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <TableRow key={i} onClick={() => setOpen(!open)} sx={{height: 60}}>
                <TableCell align={'right'}>
                    <SmartLink to={`/block/${data.height}`}>{data.height}</SmartLink>
                </TableCell>
                <TableCell align={'right'}>{formatDurationAbbr(dts, ts)} ago</TableCell>
                <TableCell align={'right'}>{data.difficulty}</TableCell>
                <TableCell align={'right'}>{data.size.toLocaleString()}</TableCell>
                <TableCell align={'right'}>{data.fullsize.toLocaleString()}</TableCell>
                <TableCell align={'right'}>{data.txn}</TableCell>
                <TableCell align={'right'}>{data.totalfee} ABEL</TableCell>
                {allowExpand && <TableCell align={'right'}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        sx={{bgcolor: 'transparent'}}
                        // onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                </TableCell>}
            </TableRow>
            {allowExpand && open && <ExpandedBlockRow height={data.height}/>}
        </>
    );
}


function ExpandedBlockRow({height}: { height: number }) {
    const txDetailQuery = useBlockTxs(height);
    return <QueryProcessor
        query={txDetailQuery}
        dataValidator={(data: any) => data.type === 'blocktxs'}
        renderer={(data: any) => (
            <TableRow key={`expanded_block_${data.height}`}>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={10}>
                    <Stack direction='column' spacing={1} sx={{maxHeight: 400, overflow: 'auto'}}>
                        {data.payload.map((tx: any) => {
                            return (
                                <BlockTxItem key={`block_${data.height}_tx_${tx.txid}`}
                                             txid={tx.txid} inputs={tx.vin.length} outputs={tx.vout.length}
                                             fee={tx.fee} version={tx.version} size={tx.size} fullSize={tx.fullsize}
                                />
                            )
                        })}
                    </Stack>
                </TableCell>
            </TableRow>)
        }
    />
}

interface BlockListProps {
    data: any[]
    allowExpand?: boolean
}

function BlockList({data, allowExpand = true}: BlockListProps) {
    // Sort by height reversely.
    data.sort((a, b) => b.height - a.height);

    return (
        <TableContainer sx={{width: {xs: 'auto', sm: 1}}}>
            <Table
                sx={{
                    width: {xs: 'max-content', sm: 1},
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': {px: 1, py: 0.5},
                }}
            >
                <TableHead sx={{'& .MuiTableCell-root': {fontWeight: 'bold'}}}>
                    <TableRow>
                        <TableCell align={'right'}>
                            Block
                        </TableCell>
                        <TableCell align={'right'}>
                            Age
                        </TableCell>
                        <TableCell align={'right'}>
                            Difficulty
                        </TableCell>
                        <TableCell align={'right'}>
                            Size
                        </TableCell>
                        <TableCell align={'right'}>
                            Full Size
                        </TableCell>
                        <TableCell align={'right'}>
                            Txn
                        </TableCell>
                        <TableCell align={'right'}>
                            Reward
                        </TableCell>
                        <TableCell align={'right'}>

                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((d, i) => <BlockRow key={d.height} allowExpand={allowExpand} data={d} i={i}/>)}
                </TableBody>
            </Table>
        </TableContainer>
    );
}


export default BlockList;
